/**
 * Projects API Service
 * Project CRUD, proposals, requests, tasks, milestones, costs, activity, payments
 */

import type {
	ApiError,
	AssignmentEscrowMetadata,
	HomeownerPropertyType,
	ProjectParticipantRole,
	PropertyStatus,
} from "@shared/types";
import type { AxiosError } from "axios";
import { apiClient } from "@/lib/api-client";
import { escrowShortfallOf, getFieldErrors } from "@/lib/errorTransformer";

const API_BASE = "/api/v1";

// ============================================================================
// TYPES
// ============================================================================

export interface HomeownerProfile {
	id: string;
	userId: string;
	name: string;
	email?: string;
	city?: string;
	locality?: string;
	profilePictureUrl?: string;
	createdAt: string;
	updatedAt: string;
}

export interface Property {
	id: string;
	homeownerId: string;
	type: HomeownerPropertyType;
	typeOther?: string | null;
	status?: PropertyStatus | null;
	address?: string;
	city?: string;
	locality?: string;
	sizeSqft?: number;
	rooms?: {
		bedrooms?: number;
		bathrooms?: number;
		livingAreas?: number;
	};
}

export interface DesignerProfile {
	id: string;
	userId: string;
	name: string;
	firmName?: string;
	bio?: string;
	profilePictureUrl?: string;
	portfolioImages?: string[];
	services?: string[];
	serviceCities?: string[];
	styles?: string[];
	priceRangeMin?: number;
	priceRangeMax?: number;
	experienceYears?: number;
	projectsCompleted?: number;
	isVerified: boolean;
}

export interface ContractorProfile {
	id: string;
	userId: string;
	name: string;
	profilePictureUrl?: string;
	trades?: string[];
	experienceYears?: number;
	serviceAreas?: string[];
	workPhotos?: string[];
	bio?: string;
	isVerified: boolean;
}

export interface Project {
	id: string;
	/**
	 * The signed-in user's side of this project (owner, designer or hired
	 * contractor), whichever role is active. Sent by GET /projects/:id.
	 */
	viewerRole?: ProjectParticipantRole;
	homeownerId: string;
	propertyId?: string;
	designerId?: string;
	title: string;
	scope: "full_home" | "partial";
	scopeDetails?: {
		rooms?: string[];
		areas?: string[];
		notes?: string;
	};
	status:
		| "draft"
		| "seeking_designer"
		| "awaiting_funding"
		| "in_progress"
		| "completed"
		| "cancelled";
	fundingStatus?: "unfunded" | "partially_funded" | "funded";
	totalFunded?: number;
	budgetMin?: number;
	budgetMax?: number;
	timelineWeeks?: number;
	startTimeline?: string;
	isPublic?: boolean;
	isPrimary?: boolean;
	/**
	 * Proposals still waiting for the homeowner to hire or decline. Set only on
	 * rows of the homeowner project list (`getProjects`).
	 */
	proposalsCount?: number;
	beforeImages?: string[];
	afterImages?: string[];
	projectImages?: string[];
	createdAt: string;
	updatedAt: string;
	property?: {
		id: string;
		type: HomeownerPropertyType;
		address?: string;
		city?: string;
		locality?: string;
		sizeSqft?: number;
		rooms?: {
			bedrooms?: number;
			bathrooms?: number;
			livingAreas?: number;
		};
	} | null;
	designer?: {
		id: string;
		userId: string;
		name: string;
		firmName?: string;
		profilePictureUrl?: string;
		styles?: string[];
	} | null;
}

export interface FundingSession {
	provider: "cashfree" | "stub";
	providerOrderId: string;
	paymentSessionId?: string;
	amount: number;
	currency: "INR";
	status: "pending";
	/**
	 * True when the server returned escrow rows an earlier attempt left
	 * `pending` instead of creating a new provider order. Such an order may
	 * already have been paid, so verification comes before checkout.
	 */
	resumed?: boolean;
	/**
	 * The Cashfree environment the backend is configured for, from its
	 * `CASHFREE_API_BASE_URL` -- the environment that will verify and settle this
	 * payment. Present only for a `cashfree` session. Compared against the
	 * build-time `VITE_CASHFREE_MODE` before checkout opens.
	 */
	environment?: "sandbox" | "production";
}

// ============================================================================
// HOMEOWNER API
// ============================================================================

export async function getHomeownerProfile(): Promise<HomeownerProfile | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/homeowners/me`);
		return response.data.data.profile;
	} catch {
		return null;
	}
}

export async function createHomeownerProfile(data: {
	name: string;
	email?: string;
	city?: string;
	locality?: string;
}): Promise<{ success: boolean; profile?: HomeownerProfile; error?: string }> {
	try {
		const response = await apiClient.post(`${API_BASE}/homeowners/me`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create profile",
		};
	}
}

export async function updateHomeownerProfile(
	data: Partial<HomeownerProfile>,
): Promise<{
	success: boolean;
	profile?: HomeownerProfile;
	error?: string;
}> {
	try {
		const response = await apiClient.patch(`${API_BASE}/homeowners/me`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update profile",
		};
	}
}

export async function getProperties(): Promise<Property[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/homeowners/me/properties`,
		);
		return response.data.data.properties;
	} catch {
		return [];
	}
}

export async function createProperty(
	data: Omit<Property, "id" | "homeownerId">,
): Promise<{
	success: boolean;
	property?: Property;
	error?: string;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/homeowners/me/properties`,
			data,
		);
		return { success: true, property: response.data.data.property };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create property",
		};
	}
}

// ============================================================================
// DESIGNER API
// ============================================================================

export async function getDesignerProfile(): Promise<DesignerProfile | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/designers/me`);
		return response.data.data.profile;
	} catch {
		return null;
	}
}

export async function createDesignerProfile(data: {
	name: string;
	firmName?: string;
	bio?: string;
	services?: string[];
	serviceCities?: string[];
	styles?: string[];
	priceRangeMin?: number;
	priceRangeMax?: number;
	experienceYears?: number;
	portfolioImages?: string[];
}): Promise<{ success: boolean; profile?: DesignerProfile; error?: string }> {
	try {
		const response = await apiClient.post(`${API_BASE}/designers/me`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create profile",
		};
	}
}

export async function browseDesigners(params?: {
	query?: string;
	city?: string;
	styles?: string[];
	budgetMin?: number;
	budgetMax?: number;
	page?: number;
	limit?: number;
}): Promise<{
	designers: DesignerProfile[];
	total: number;
	page: number;
	totalPages: number;
}> {
	try {
		const response = await apiClient.get(`${API_BASE}/designers`, { params });
		return response.data.data;
	} catch {
		return { designers: [], total: 0, page: 1, totalPages: 0 };
	}
}

export async function getFeaturedDesigners(
	city?: string,
): Promise<DesignerProfile[]> {
	try {
		const response = await apiClient.get(`${API_BASE}/designers/featured`, {
			params: city ? { city } : undefined,
		});
		return response.data.data.designers;
	} catch {
		return [];
	}
}

export async function getDesignerById(
	id: string,
): Promise<DesignerProfile | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/designers/${id}`);
		return response.data.data.designer;
	} catch {
		return null;
	}
}

// ============================================================================
// CONTRACTOR API
// ============================================================================

export async function getContractorProfile(): Promise<ContractorProfile | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/contractors/me`);
		return response.data.data.profile;
	} catch {
		return null;
	}
}

export async function createContractorProfile(data: {
	name: string;
	trades?: string[];
	bio?: string;
	serviceAreas?: string[];
	experienceYears?: number;
	workPhotos?: string[];
}): Promise<{ success: boolean; profile?: ContractorProfile; error?: string }> {
	try {
		const response = await apiClient.post(`${API_BASE}/contractors/me`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create profile",
		};
	}
}

export async function browseContractors(params?: {
	query?: string;
	trades?: string[];
	city?: string;
	page?: number;
	limit?: number;
}): Promise<{
	contractors: ContractorProfile[];
	total: number;
	page: number;
	totalPages: number;
}> {
	try {
		const response = await apiClient.get(`${API_BASE}/contractors`, { params });
		return response.data.data;
	} catch {
		return { contractors: [], total: 0, page: 1, totalPages: 0 };
	}
}

export async function getContractorById(
	id: string,
): Promise<ContractorProfile | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/contractors/${id}`);
		return response.data.data.contractor;
	} catch {
		return null;
	}
}

// ============================================================================
// PROJECT API
// ============================================================================

/**
 * The homeowner's projects. Unlike `getProjects` a failed request throws, for
 * a caller that has to tell "no projects" from "could not load them".
 */
export async function listMyProjects(status?: string): Promise<Project[]> {
	const response = await apiClient.get(`${API_BASE}/projects`, {
		params: status ? { status } : undefined,
	});
	return response.data.data.projects;
}

export async function getProjects(status?: string): Promise<Project[]> {
	try {
		return await listMyProjects(status);
	} catch {
		return [];
	}
}

export async function getMyProjects(): Promise<Project[]> {
	return getProjects();
}

export async function getDesignerProjects(
	status?: string,
	options?: { signal?: AbortSignal },
): Promise<Project[]> {
	try {
		const response = await apiClient.get(`${API_BASE}/projects/designer`, {
			params: status ? { status } : undefined,
			signal: options?.signal,
		});
		return response.data.data.projects;
	} catch {
		return [];
	}
}

export async function getProjectById(id: string): Promise<Project | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/projects/${id}`);
		return response.data.data.project;
	} catch {
		return null;
	}
}

export async function createProject(data: {
	title: string;
	scope: "full_home" | "partial";
	propertyId?: string;
	scopeDetails?: { rooms?: string[]; areas?: string[]; notes?: string };
	budgetMin?: number;
	budgetMax?: number;
	timelineWeeks?: number;
	startTimeline?: string;
	beforeImages?: string[];
	afterImages?: string[];
	isPublic?: boolean;
}): Promise<{ success: boolean; project?: Project; error?: string }> {
	try {
		const response = await apiClient.post(`${API_BASE}/projects`, data);
		return { success: true, project: response.data.data.project };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create project",
		};
	}
}

export async function updateProject(
	id: string,
	data: Partial<Project>,
	options?: { signal?: AbortSignal },
): Promise<{ success: boolean; project?: Project; error?: string }> {
	try {
		const response = await apiClient.patch(`${API_BASE}/projects/${id}`, data, {
			signal: options?.signal,
		});
		return { success: true, project: response.data.data.project };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update project",
		};
	}
}

export async function completeProject(
	id: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(`${API_BASE}/projects/${id}/complete`);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to complete project",
		};
	}
}

export async function cancelProject(
	id: string,
	reason?: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(
			`${API_BASE}/projects/${id}/cancel`,
			reason ? { reason } : undefined,
		);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to cancel project",
		};
	}
}

export async function toggleProjectPublic(
	projectId: string,
): Promise<{ success: boolean; project?: Project; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/projects/${projectId}/toggle-public`,
		);
		return { success: true, project: response.data.data.project };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error:
				err.response?.data?.message || "Failed to toggle project visibility",
		};
	}
}

export async function setPrimaryProject(
	projectId: string,
): Promise<{ success: boolean; project?: Project; error?: string }> {
	try {
		const response = await apiClient.patch(
			`${API_BASE}/projects/${projectId}/primary`,
		);
		return { success: true, project: response.data.data.project };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to set primary project",
		};
	}
}

export async function updateProjectTransformationImages(
	projectId: string,
	data: { beforeImages: string[]; afterImages: string[] },
): Promise<{ success: boolean; project?: Project; error?: string }> {
	try {
		const response = await apiClient.patch(
			`${API_BASE}/projects/${projectId}/transformation-images`,
			data,
		);
		return { success: true, project: response.data.data.project };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error:
				err.response?.data?.message || "Failed to update transformation images",
		};
	}
}

// ============================================================================
// REQUEST & PROPOSAL API
// ============================================================================

export async function sendRequestToDesigner(data: {
	projectId: string;
	designerId: string;
	message?: string;
}): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(`${API_BASE}/requests`, data);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to send request",
		};
	}
}

/**
 * The project's designer requests, each with its proposal when one was
 * submitted. Throws on failure, so a caller can tell a failed load from a
 * project nobody has been asked about.
 */
export async function fetchProjectProposals(
	projectId: string,
): Promise<ProjectProposalItem[]> {
	const response = await apiClient.get(
		`${API_BASE}/requests/project/${projectId}/proposals`,
	);
	return response.data.data.proposals;
}

/** `fetchProjectProposals`, resolving `[]` instead of throwing on failure. */
export async function getProjectProposals(
	projectId: string,
): Promise<ProjectProposalItem[]> {
	try {
		return await fetchProjectProposals(projectId);
	} catch {
		return [];
	}
}

// ============================================================================
// REQUEST & PROPOSAL EXTENDED API
// ============================================================================

export interface ProjectRequest {
	id: string;
	projectId: string;
	designerId: string;
	homeownerId: string;
	message?: string;
	status: "pending" | "proposal_submitted" | "accepted" | "rejected";
	createdAt: string;
}

export interface Proposal {
	id: string;
	requestId: string;
	designerId: string;
	costEstimate: number;
	timelineWeeks: number;
	scopeDescription?: string;
	approach?: string;
	notes?: string;
	costBreakdown?: {
		designFees?: number;
		labor?: number;
		materials?: number;
		other?: number;
	};
	status?: "submitted" | "accepted" | "rejected";
	createdAt: string;
}

export interface ProjectProposalItem {
	request: ProjectRequest;
	proposal: Proposal | null;
	designer: DesignerProfile;
}

/**
 * A row of GET /api/v1/requests/designer.
 *
 * Mirrors `DesignerRequestListItem` in the backend's requests service: that
 * endpoint joins the project, its homeowner and its property, so its rows carry
 * fields no other request read returns. They are declared here rather than on
 * `ProjectRequest` because `getProjectRequests` and `ProjectProposalItem` read
 * endpoints that do not join them.
 */
export interface DesignerRequestListItem extends ProjectRequest {
	/** The project owner's display name. */
	homeownerName: string;
	/** The property's city, or null when the project has no property attached. */
	propertyCity: string | null;
	/** The property's size, or null when the project has no property attached. */
	propertySizeSqft: number | null;
	/** The budget is held on the project, and either end of it may be unset. */
	projectBudgetMin: number | null;
	/** The budget is held on the project, and either end of it may be unset. */
	projectBudgetMax: number | null;
}

/**
 * The requests homeowners have sent to the signed-in designer.
 *
 * Throws on failure rather than returning `[]`, so callers can tell a broken
 * request from a designer who has genuinely received none — the dashboard
 * renders a retry for the first and an empty state for the second.
 */
export async function getDesignerRequests(options?: {
	signal?: AbortSignal;
}): Promise<DesignerRequestListItem[]> {
	const response = await apiClient.get(`${API_BASE}/requests/designer`, {
		signal: options?.signal,
	});
	return response.data.data.requests;
}

/**
 * How many proposals the signed-in designer has submitted.
 *
 * Throws on failure rather than returning `0`, which would report a
 * measurement the request never made.
 */
export async function getDesignerProposalCount(): Promise<number> {
	const response = await apiClient.get(
		`${API_BASE}/requests/designer/proposals/count`,
	);
	return response.data.data.count;
}

/** The project's designer requests. Throws on failure. */
export async function getProjectRequests(
	projectId: string,
): Promise<ProjectRequest[]> {
	const response = await apiClient.get(
		`${API_BASE}/requests/project/${projectId}`,
	);
	return response.data.data.requests;
}

export async function submitProposal(
	requestId: string,
	data: {
		costEstimate: number;
		timelineWeeks: number;
		scopeDescription?: string;
		approach?: string;
		notes?: string;
		costBreakdown?: {
			designFees?: number;
			labor?: number;
			materials?: number;
			other?: number;
		};
	},
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(`${API_BASE}/requests/${requestId}/proposal`, data);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to submit proposal",
		};
	}
}

export async function acceptProposal(
	proposalId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(`${API_BASE}/requests/proposals/${proposalId}/accept`);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to accept proposal",
		};
	}
}

export async function rejectProposal(
	proposalId: string,
	options?: { signal?: AbortSignal },
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(
			`${API_BASE}/requests/proposals/${proposalId}/reject`,
			{},
			{
				signal: options?.signal,
			},
		);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to reject proposal",
		};
	}
}

// ============================================================================
// TASKS API
// ============================================================================

export interface Task {
	id: string;
	projectId: string;
	title: string;
	description?: string;
	status: "todo" | "in_progress" | "completed";
	assignedTo?: string;
	dueDate?: string;
	/** The milestone of this project the task belongs to; null for none. */
	milestoneId: string | null;
	createdAt: string;
	assigneeDisplayName: string;
	assigneeProfileType: "homeowner" | "designer" | "contractor" | null;
}

export async function getProjectTasks(projectId: string): Promise<Task[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/projects/${projectId}/tasks`,
		);
		return response.data.data.tasks ?? [];
	} catch {
		return [];
	}
}

export async function createTask(
	projectId: string,
	data: {
		title: string;
		description?: string;
		assignedTo?: string;
		dueDate?: string;
		milestoneId?: string;
	},
): Promise<{ success: boolean; task?: Task; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/projects/${projectId}/tasks`,
			data,
		);
		return { success: true, task: response.data.data.task };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create task",
		};
	}
}

export async function updateTaskStatus(
	taskId: string,
	status: Task["status"],
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.patch(`${API_BASE}/tasks/${taskId}/status`, { status });
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update task",
		};
	}
}

export async function deleteTask(
	taskId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.delete(`${API_BASE}/tasks/${taskId}`);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to delete task",
		};
	}
}

// ============================================================================
// MILESTONES API
// ============================================================================

export interface Milestone {
	id: string;
	projectId: string;
	title: string;
	description?: string;
	paymentAmount: number | null;
	targetDate?: string | null;
	paymentStatus: "not_paid" | "paid";
	fundingStatus: "unfunded" | "funded" | "released" | "disputed" | "refunded";
	paidAt?: string | null;
	status: "pending" | "completed";
	completedAt?: string | null;
	orderIndex: number;
	createdAt: string;
}

export async function getProjectMilestones(
	projectId: string,
): Promise<Milestone[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/projects/${projectId}/milestones`,
		);
		return response.data.data.milestones ?? [];
	} catch {
		return [];
	}
}

export async function createMilestone(
	projectId: string,
	data: {
		title: string;
		description?: string;
		paymentAmount: number;
		targetDate?: string;
	},
): Promise<{ success: boolean; milestone?: Milestone; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/projects/${projectId}/milestones`,
			data,
		);
		return { success: true, milestone: response.data.data.milestone };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create milestone",
		};
	}
}

export async function markMilestonePaid(
	milestoneId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(`${API_BASE}/milestones/${milestoneId}/mark-paid`, {
			paymentStatus: "paid",
		});
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to mark milestone paid",
		};
	}
}

export async function updateMilestone(
	milestoneId: string,
	data: Partial<Pick<Milestone, "title" | "paymentAmount" | "targetDate">>,
): Promise<{ success: boolean; milestone?: Milestone; error?: string }> {
	try {
		const response = await apiClient.patch(
			`${API_BASE}/milestones/${milestoneId}`,
			data,
		);
		return { success: true, milestone: response.data.data.milestone };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update milestone",
		};
	}
}

export async function deleteMilestone(
	milestoneId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.delete(`${API_BASE}/milestones/${milestoneId}`);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to delete milestone",
		};
	}
}

export async function updateMilestoneStatus(
	milestoneId: string,
	status: "pending" | "completed",
): Promise<{ success: boolean; milestone?: Milestone; error?: string }> {
	try {
		const response = await apiClient.patch(
			`${API_BASE}/milestones/${milestoneId}/status`,
			{ status },
		);
		return { success: true, milestone: response.data.data.milestone };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update milestone status",
		};
	}
}

// ============================================================================
// COST ESTIMATES API
// ============================================================================

export interface CostEstimate {
	id: string;
	projectId: string;
	category: "design_fees" | "labor" | "materials" | "miscellaneous";
	description: string;
	estimatedAmount: number;
	actualAmount: number | null;
	createdAt: string;
}

export async function getProjectCosts(
	projectId: string,
): Promise<CostEstimate[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/projects/${projectId}/costs`,
		);
		return response.data.data.costEstimates ?? [];
	} catch {
		return [];
	}
}

export async function createCostEstimate(
	projectId: string,
	data: {
		category: CostEstimate["category"];
		description: string;
		estimatedAmount: number;
		actualAmount?: number;
	},
): Promise<{ success: boolean; cost?: CostEstimate; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/projects/${projectId}/costs`,
			data,
		);
		return { success: true, cost: response.data.data.costEstimate };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create cost estimate",
		};
	}
}

export async function updateCostEstimate(
	costEstimateId: string,
	data: Partial<
		Pick<CostEstimate, "description" | "estimatedAmount" | "actualAmount">
	>,
): Promise<{ success: boolean; cost?: CostEstimate; error?: string }> {
	try {
		const response = await apiClient.patch(
			`${API_BASE}/costs/${costEstimateId}`,
			data,
		);
		return { success: true, cost: response.data.data.costEstimate };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update cost estimate",
		};
	}
}

export async function deleteCostEstimate(
	costEstimateId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.delete(`${API_BASE}/costs/${costEstimateId}`);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to delete cost estimate",
		};
	}
}

// ============================================================================
// ACTIVITY LOG API
// ============================================================================

export interface ActivityLog {
	id: string;
	projectId: string;
	userId: string;
	action: string;
	details?: string | Record<string, unknown>;
	createdAt: string;
}

export async function getProjectActivity(
	projectId: string,
): Promise<ActivityLog[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/projects/${projectId}/activity`,
		);
		return response.data.data.activities ?? [];
	} catch {
		return [];
	}
}

// ============================================================================
// PROJECT CONTRACTORS API
// ============================================================================

export interface ProjectContractorItem {
	id: string;
	projectId: string;
	contractorId: string;
	invitedBy?: string;
	status:
		| "invited"
		| "interested"
		| "quote_submitted"
		| "hired"
		| "completion_requested"
		| "completed"
		| "removed"
		| "declined";
	quoteAmount?: number;
	quoteDetails?: string;
	message?: string;
	/**
	 * Who pays the contractor, fixed when they are hired and null before:
	 * `platform_paid` from the project's escrow when the homeowner hired them,
	 * `designer_paid` by the designer directly when the designer did.
	 */
	paymentArrangement?: "platform_paid" | "designer_paid" | null;
	hiredAt?: string;
	completedAt?: string;
	createdAt: string;
	updatedAt: string;
	contractor?: ContractorProfile;
	project?: Project;
}

export async function getProjectContractors(projectId: string): Promise<{
	contractors: ProjectContractorItem[];
	disputeWindowHours: number;
}> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/projects/${projectId}/contractors`,
		);
		return {
			contractors: response.data.data.contractors ?? [],
			disputeWindowHours: response.data.data.disputeWindowHours ?? 72,
		};
	} catch {
		return { contractors: [], disputeWindowHours: 72 };
	}
}

export async function inviteContractor(
	projectId: string,
	contractorId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(`${API_BASE}/projects/${projectId}/contractors`, {
			contractorId,
		});
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to invite contractor",
		};
	}
}

/**
 * What a refused hire says. `shortfall` is set only on the backend's
 * `ESCROW_SHORTFALL` refusal: the rupees the project's escrow is short of the
 * quote, which adding funds (`topUpEscrow`) makes up.
 */
export interface HireContractorResult {
	success: boolean;
	error?: string;
	shortfall?: number;
}

export async function hireContractor(
	invitationId: string,
): Promise<HireContractorResult> {
	try {
		await apiClient.post(
			`${API_BASE}/project-contractors/${invitationId}/hire`,
		);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		const shortfall = escrowShortfallOf(error);
		return {
			success: false,
			error: err.response?.data?.message || "Failed to hire contractor",
			...(shortfall !== undefined ? { shortfall } : {}),
		};
	}
}

export async function removeContractor(
	invitationId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.delete(`${API_BASE}/project-contractors/${invitationId}`);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to remove contractor",
		};
	}
}

export async function getContractorAssignments(): Promise<
	ProjectContractorItem[]
> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/contractors/me/assignments`,
		);
		return response.data.data.assignments;
	} catch {
		return [];
	}
}

export async function getContractorInvitations(
	status?: string,
): Promise<ProjectContractorItem[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/contractors/me/invitations`,
			{
				params: status ? { status } : undefined,
			},
		);
		return response.data.data.invitations;
	} catch {
		return [];
	}
}

export async function getInvitationDetails(
	invitationId: string,
): Promise<ProjectContractorItem | null> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/contractors/me/invitations/${invitationId}`,
		);
		return response.data.data.invitation;
	} catch {
		return null;
	}
}

export async function submitContractorQuote(
	invitationId: string,
	data: { quoteAmount: number; quoteDetails?: string },
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(
			`${API_BASE}/contractors/me/invitations/${invitationId}/quote`,
			data,
		);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to submit quote",
		};
	}
}

export async function declineInvitation(
	invitationId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(
			`${API_BASE}/contractors/me/invitations/${invitationId}/decline`,
		);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to decline invitation",
		};
	}
}

// ============================================================================
// PUBLIC PROJECTS & INTEREST API
// ============================================================================

export async function expressInterest(data: {
	projectId: string;
	quoteAmount: number;
	quoteDetails?: string;
	message?: string;
}): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(`${API_BASE}/contractors/me/interest`, data);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to express interest",
		};
	}
}

// ============================================================================
// PROFILE UPDATE API
// ============================================================================

export async function updateDesignerProfile(
	data: Partial<Omit<DesignerProfile, "id" | "userId" | "isVerified">>,
): Promise<{ success: boolean; profile?: DesignerProfile; error?: string }> {
	try {
		const response = await apiClient.patch(`${API_BASE}/designers/me`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update profile",
		};
	}
}

export async function updateContractorProfile(
	data: Partial<Omit<ContractorProfile, "id" | "userId" | "isVerified">>,
): Promise<{ success: boolean; profile?: ContractorProfile; error?: string }> {
	try {
		const response = await apiClient.patch(`${API_BASE}/contractors/me`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update profile",
		};
	}
}

// ============================================================================
// UPLOAD API
// ============================================================================

export async function uploadFile(
	file: File,
	options?: { folder?: string; projectId?: string },
): Promise<{ success: boolean; url?: string; error?: string }> {
	try {
		const formData = new FormData();
		formData.append("file", file);
		const params: Record<string, string> = {};
		if (options?.folder) params.folder = options.folder;
		if (options?.projectId) {
			params.entityType = "projects";
			params.entityId = options.projectId;
		}
		const response = await apiClient.post(`${API_BASE}/upload`, formData, {
			params: Object.keys(params).length > 0 ? params : undefined,
		});
		return { success: true, url: response.data.data.url };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to upload file",
		};
	}
}

export async function uploadMultipleFiles(
	files: File[],
	options?: { folder?: string; projectId?: string },
): Promise<{ success: boolean; urls?: string[]; error?: string }> {
	try {
		const formData = new FormData();
		for (const file of files) {
			formData.append("files", file);
		}
		const params: Record<string, string> = {};
		if (options?.folder) params.folder = options.folder;
		if (options?.projectId) {
			params.entityType = "projects";
			params.entityId = options.projectId;
		}
		const response = await apiClient.post(
			`${API_BASE}/upload/multiple`,
			formData,
			{ params: Object.keys(params).length > 0 ? params : undefined },
		);
		const data = response.data?.data ?? {};
		const results = data.results as { url: string }[] | undefined;
		const urls: string[] = results
			? results.map((r: { url: string }) => r.url)
			: (data.urls ?? []);
		return { success: true, urls };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to upload files",
		};
	}
}

// ============================================================================
// ADMIN API
// ============================================================================

export async function getUnverifiedDesigners(): Promise<DesignerProfile[]> {
	try {
		const response = await apiClient.get(`${API_BASE}/admin/designers/pending`);
		return response.data.data.designers;
	} catch {
		return [];
	}
}

export async function getUnverifiedContractors(): Promise<ContractorProfile[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/admin/contractors/pending`,
		);
		return response.data.data.contractors ?? [];
	} catch {
		return [];
	}
}

export async function verifyDesigner(
	id: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(`${API_BASE}/admin/designers/${id}/verify`);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to verify designer",
		};
	}
}

export async function rejectDesigner(
	id: string,
	reason?: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(`${API_BASE}/admin/designers/${id}/reject`, {
			reason,
		});
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to reject designer",
		};
	}
}

export async function verifyContractor(
	id: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(`${API_BASE}/admin/contractors/${id}/verify`);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to verify contractor",
		};
	}
}

export async function rejectContractor(
	id: string,
	reason?: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(`${API_BASE}/admin/contractors/${id}/reject`, {
			reason,
		});
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to reject contractor",
		};
	}
}

// ============================================================================
// NOTIFICATIONS API
// ============================================================================
export {
	createRealtimeAuthToken,
	getNotifications,
	markAllNotificationsRead,
	markNotificationRead,
	type Notification,
	type NotificationsQuery,
} from "@/features/settings/services/notifications-api.service";

// ============================================================================
// SEARCH API (Typesense)
// ============================================================================

export async function searchDesigners(params: {
	query?: string;
	city?: string;
	styles?: string[];
	budgetMin?: number;
	budgetMax?: number;
	page?: number;
	perPage?: number;
}): Promise<{
	designers: DesignerProfile[];
	total: number;
	page: number;
	totalPages: number;
}> {
	try {
		const response = await apiClient.get(`${API_BASE}/search/designers`, {
			params,
		});
		return response.data.data;
	} catch {
		return { designers: [], total: 0, page: 1, totalPages: 0 };
	}
}

export async function searchContractors(params: {
	query?: string;
	trades?: string[];
	city?: string;
	page?: number;
	perPage?: number;
}): Promise<{
	contractors: ContractorProfile[];
	total: number;
	page: number;
	totalPages: number;
}> {
	try {
		const response = await apiClient.get(`${API_BASE}/search/contractors`, {
			params,
		});
		return response.data.data;
	} catch {
		return { contractors: [], total: 0, page: 1, totalPages: 0 };
	}
}

// ============================================================================
// PAYMENTS API (Escrow)
// ============================================================================

/** Where one money leg of a dispute resolution stands. */
export type DisputeLegState =
	| "not_started"
	| "pending"
	| "unconfirmed"
	| "failed"
	| "succeeded";

export interface EscrowTransaction {
	id: string;
	projectId: string;
	milestoneId?: string;
	payerId: string;
	payeeId?: string;
	amount: number;
	platformFee: number;
	payeeAmount: number;
	status:
		| "pending"
		| "captured"
		| "held"
		| "released"
		| "refunded"
		| "partially_refunded"
		| "failed";
	providerOrderId?: string;
	providerPaymentId?: string;
	providerTransferId?: string;
	/** Rupees a dispute resolution paid out to the payee; 0 otherwise. */
	releasedAmount?: number;
	/** Rupees a dispute resolution refunded to the payer; 0 otherwise. */
	refundedAmount?: number;
	/**
	 * Where the payout to the designer of the resolution on the row's latest
	 * dispute stands; null when the row has no dispute or its resolution pays
	 * the designer nothing.
	 */
	disputePayoutState?: DisputeLegState | null;
	/**
	 * Where the refund of the resolution on the row's latest dispute stands;
	 * null when the row has no dispute or its resolution refunds nothing.
	 */
	disputeRefundState?: DisputeLegState | null;
	heldAt?: string;
	releasedAt?: string;
	refundedAt?: string;
	autoReleaseAt?: string;
	milestoneTitle?: string | null;
	projectTitle?: string;
	metadata?: AssignmentEscrowMetadata | Record<string, unknown> | null;
	createdAt: string;
	updatedAt: string;
}

export interface PaymentsSummary {
	totalFunded: number;
	totalHeld: number;
	totalReleased: number;
	/**
	 * What a contractor the homeowner hires can be paid from: the escrow left
	 * once the designer's funded, unreleased milestones and the contractors
	 * already hired are set aside (`getEscrowCover` in the backend). Absent on
	 * the zeroed summary `getProjectPayments` returns when its request fails,
	 * so a failed load does not read as nothing available.
	 */
	availableForHire?: number;
}

export interface PayoutAccount {
	id: string;
	userId: string;
	accountHolderName: string;
	accountNumber: string;
	ifscCode: string;
	upiId?: string;
	isVerified: boolean;
	providerAccountId?: string;
	/**
	 * "synced" once the payment provider holds the account (it has a provider
	 * account id, and not one the test provider wrote while another provider
	 * is active). A failed sync is never stored: the account is not saved.
	 */
	syncStatus?: "pending" | "synced";
	verifiedAt?: string;
	createdAt: string;
	updatedAt: string;
}

export async function fundProject(
	projectId: string,
	milestoneIds?: string[],
): Promise<{
	success: boolean;
	fundingSession?: FundingSession;
	error?: string;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/projects/${projectId}/fund`,
			milestoneIds && milestoneIds.length > 0 ? { milestoneIds } : {},
		);
		return {
			success: true,
			fundingSession: response.data.data.fundingSession,
		};
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to fund project",
		};
	}
}

/**
 * Add money to a project's escrow that no milestone asks for, so a contractor
 * can be hired from it. `amount` is whole rupees, sent as given. The session
 * has `fundProject`'s shape and is verified the same way, through
 * `verifyProjectFunding`.
 */
export async function topUpEscrow(
	projectId: string,
	amount: number,
): Promise<{
	success: boolean;
	fundingSession?: FundingSession;
	error?: string;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/projects/${projectId}/escrow/top-up`,
			{ amount },
		);
		return {
			success: true,
			fundingSession: response.data.data.fundingSession,
		};
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to add funds",
		};
	}
}

export async function verifyProjectFunding(
	// Optional on the bank-redirect return path: Cashfree echoes only the
	// order id there, and the backend resolves the project from the funding
	// session (still access-checked) and returns it.
	projectId: string | undefined,
	providerOrderId: string,
): Promise<{
	success: boolean;
	verified: boolean;
	projectId?: string;
	// A top-up order reports the project's status as it is, which need not be
	// `in_progress`.
	projectStatus?: Project["status"];
	transactions?: EscrowTransaction[];
	error?: string;
}> {
	try {
		const response = await apiClient.post(`${API_BASE}/payments/verify`, {
			projectId,
			providerOrderId,
		});

		return {
			success: true,
			verified: response.data.data.verified,
			projectId: response.data.data.projectId,
			projectStatus: response.data.data.projectStatus,
			transactions: response.data.data.transactions,
		};
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			verified: false,
			error: err.response?.data?.message || "Failed to verify project funding",
		};
	}
}

export async function releaseMilestonePayment(milestoneId: string): Promise<{
	success: boolean;
	transaction?: EscrowTransaction;
	error?: string;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/milestones/${milestoneId}/release`,
		);
		return { success: true, transaction: response.data.data.transaction };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to release payment",
		};
	}
}

export async function getProjectPayments(
	projectId: string,
): Promise<{ transactions: EscrowTransaction[]; summary: PaymentsSummary }> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/projects/${projectId}/payments`,
		);
		return response.data.data;
	} catch {
		return {
			transactions: [],
			summary: { totalFunded: 0, totalHeld: 0, totalReleased: 0 },
		};
	}
}

/**
 * The signed-in user's escrow transactions, as payer or payee.
 *
 * Throws on failure rather than returning `[]`, so callers can tell a broken
 * request from a user who genuinely has no payments — the designer dashboard
 * renders a retry for the first and an empty state for the second.
 */
export async function getPaymentHistory(): Promise<EscrowTransaction[]> {
	const response = await apiClient.get(`${API_BASE}/payments/history`);
	return response.data.data.transactions;
}

export async function createPayoutAccount(data: {
	accountHolderName: string;
	accountNumber: string;
	ifscCode: string;
	upiId?: string;
}): Promise<{
	success: boolean;
	payoutAccount?: PayoutAccount;
	error?: string;
	fieldErrors?: Record<string, string>;
}> {
	try {
		const response = await apiClient.post(`${API_BASE}/payout-accounts`, data, {
			inlineErrors: true,
		});
		return { success: true, payoutAccount: response.data.data.payoutAccount };
	} catch (error: unknown) {
		const axiosError = error as AxiosError<ApiError>;
		return {
			success: false,
			error:
				axiosError.response?.data?.message || "Failed to create payout account",
			fieldErrors: getFieldErrors(axiosError) || undefined,
		};
	}
}

/**
 * The signed-in user's payout account, or `null` when they have not added one.
 * `GET /payout-accounts/me` answers 200 with `payoutAccount: null` for that, so
 * a thrown error is always a failed request, and it is rethrown.
 */
export async function fetchMyPayoutAccount(): Promise<PayoutAccount | null> {
	const response = await apiClient.get(`${API_BASE}/payout-accounts/me`, {
		inlineErrors: true,
	});
	return response.data.data.payoutAccount;
}

/** `fetchMyPayoutAccount` with a failed request flattened to `null`. */
export async function getMyPayoutAccount(): Promise<PayoutAccount | null> {
	try {
		return await fetchMyPayoutAccount();
	} catch {
		return null;
	}
}

export async function updatePayoutAccount(
	id: string,
	data: {
		accountHolderName?: string;
		accountNumber?: string;
		ifscCode?: string;
		upiId?: string;
	},
): Promise<{
	success: boolean;
	payoutAccount?: PayoutAccount;
	error?: string;
	fieldErrors?: Record<string, string>;
}> {
	try {
		const response = await apiClient.patch(
			`${API_BASE}/payout-accounts/${id}`,
			data,
			{ inlineErrors: true },
		);
		return { success: true, payoutAccount: response.data.data.payoutAccount };
	} catch (error: unknown) {
		const axiosError = error as AxiosError<ApiError>;
		return {
			success: false,
			error:
				axiosError.response?.data?.message || "Failed to update payout account",
			fieldErrors: getFieldErrors(axiosError) || undefined,
		};
	}
}

// ============================================================================
// SHORTLIST API
// ============================================================================

/**
 * Re-exported from the quotes service so there is ONE shape.
 *
 * The old local interface described the JSONB blob quotes used to live in --
 * free-text vendor, free-text unit, a rating that was seeded 4.8 and meant
 * nothing. Keeping a second copy of that shape here is how two sources of truth
 * start.
 */
export type { ShortlistQuote } from "./quotes-api.service";

export interface ShortlistEntry {
	id: string;
	projectId: string;
	productId: string;
	variantId?: string;
	quantity: number;
	notes?: string;
	quotes?: import("./quotes-api.service").ShortlistQuote[];
	/** The approved quote's ID. Was `selectedQuoteIndex` -- a position, not an identity. */
	selectedQuoteId?: string | null;
	createdAt: string;
	updatedAt?: string;
	product?: {
		name: string;
		images?: string[];
		brand?: { name: string };
	};
	variant?: {
		variantAttributes: Record<string, string>;
	};
}

export async function getProjectShortlist(
	projectId: string,
): Promise<ShortlistEntry[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/projects/${projectId}/shortlist`,
		);
		return response.data.data.shortlist;
	} catch {
		return [];
	}
}

export async function addToShortlist(
	projectId: string,
	data: {
		productId: string;
		variantId?: string;
		quantity?: number;
		notes?: string;
	},
): Promise<{ success: boolean; entry?: ShortlistEntry; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/projects/${projectId}/shortlist`,
			data,
		);
		return { success: true, entry: response.data.data.entry };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to add to shortlist",
		};
	}
}

export async function updateShortlistEntry(
	projectId: string,
	entryId: string,
	data: {
		quantity?: number;
		notes?: string;
		variantId?: string;
	},
): Promise<{ success: boolean; entry?: ShortlistEntry; error?: string }> {
	try {
		const response = await apiClient.patch(
			`${API_BASE}/projects/${projectId}/shortlist/${entryId}`,
			data,
		);
		return { success: true, entry: response.data.data.entry };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update shortlist entry",
		};
	}
}

export async function removeFromShortlist(
	projectId: string,
	entryId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.delete(
			`${API_BASE}/projects/${projectId}/shortlist/${entryId}`,
		);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to remove from shortlist",
		};
	}
}

// ============================================================================
// DASHBOARD (Designer/Contractor enriched view)
// ============================================================================

export interface DashboardProject {
	id: string;
	title: string;
	status: string;
	scope: string;
	budgetMin: number | null;
	budgetMax: number | null;
	timelineWeeks: number | null;
	startTimeline: string | null;
	createdAt: string;
	updatedAt: string;
	client: { name: string };
	property: {
		city: string | null;
		locality: string | null;
		sizeSqft: number | null;
		type: string;
	} | null;
	progress: {
		total: number;
		completed: number;
		percentage: number;
	};
	totalFunded: number;
}

export interface DashboardStats {
	active: number;
	pendingApproval: number;
	completed: number;
	totalProjectValue: number;
	paymentsPending: number;
}

export interface DashboardResponse {
	projects: DashboardProject[];
	stats: DashboardStats;
	filterOptions: { cities: string[] };
	pagination: { page: number; limit: number; total: number };
}

export async function getProjectsDashboard(params?: {
	page?: number;
	limit?: number;
}): Promise<DashboardResponse> {
	const query = new URLSearchParams();
	if (params?.page) query.set("page", String(params.page));
	if (params?.limit) query.set("limit", String(params.limit));
	const qs = query.toString();
	const url = `${API_BASE}/projects/dashboard${qs ? `?${qs}` : ""}`;
	const response = await apiClient.get(url);
	return response.data.data;
}
